/**
 * Home's side column (TMMAPP-0152 §A 9–10; P:156–181).
 *
 * - **Today's gentle nudge** is a region: the leaf in `warningFg`, the rule's line (`nudgeOf`, a
 *   fixed rule list, never AI text), its primary button and **Not now**, which hides it until
 *   tomorrow and leaves the calm line. The calm line has nothing to hide, so it has no Not now.
 * - **Coming up** (capitals label): the next meeting in scope, "{title}" over
 *   "{date} · {time} · {duration}", which opens the meeting's session. Live sessions are Community's and
 *   aren't listed. "Nothing scheduled." when there is none.
 */
import { useId } from 'react';
import { Box, Button, Paper, Skeleton, Typography, useTheme } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { MeetingCalendar, MeetingCalendarEntry } from '@/api/meetings';
import { ErrorState } from '@/components/ErrorState';
import { LeafIcon, MeetingsIcon } from '@/components/icons';
import { formatCivilDate, formatInstant, formatRelativeDays } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { utcToZonedCivil } from '@/i18n/zonedCivil';
import { tintFor } from '@/theme/categorical';
import { resolveTokens } from '@/theme/theme';
import { meetingDateTime, meetingMinutes } from './homeFormat';
import { meetingSessionPath } from '@/features/meetings/nextMeeting';
import { civilDaysBetween, type Nudge } from './homeRules';
import type { Read } from './useHomeData';

const CARD_SX = {
  p: '20px',
  borderRadius: '20px',
  display: 'flex',
  flexDirection: 'column',
} as const;

export function NudgeCard({
  nudge,
  timeZone,
  today,
  onDismiss,
}: {
  /** `undefined` while the reads it depends on load. */
  nudge: Nudge | undefined;
  timeZone: string;
  today: string;
  onDismiss: () => void;
}) {
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  const titleId = useId();

  let line: string | null = null;
  let cta: { label: string; to: string } | null = null;
  if (nudge !== undefined) {
    switch (nudge.kind) {
      case 'meeting': {
        const start = nudge.entry.startsAt;
        const days = nudge.entry.live
          ? 0
          : Math.max(0, civilDaysBetween(today, utcToZonedCivil(start, timeZone).date));
        line = t('home.nudge.meeting', {
          day: formatRelativeDays(days, locale),
          time: formatInstant(start, locale, timeZone, { timeStyle: 'short' }),
        });
        cta = { label: t('home.nudge.getReady'), to: meetingSessionPath(nudge.entry) };
        break;
      }
      case 'review':
        line = t('home.nudge.review', { count: nudge.count });
        cta = { label: t('home.nudge.reviewNow'), to: '/me/accounts' };
        break;
      case 'unallocated':
        line = t('home.nudge.unallocated', { count: nudge.count });
        cta = { label: t('home.nudge.allocate'), to: '/me/accounts' };
        break;
      case 'calm':
        line = t('home.nudge.calm', {
          weekday: formatCivilDate(today, locale, { weekday: 'long' }),
        });
        cta = { label: t('home.nudge.openTools'), to: '/me/tools' };
        break;
    }
  }

  return (
    <Paper
      component="section"
      aria-labelledby={titleId}
      data-nudge={nudge?.kind ?? 'loading'}
      sx={{ ...CARD_SX, gap: '12px' }}
    >
      <Typography
        id={titleId}
        variant="subtitle2"
        component="h2"
        sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600 }}
      >
        <Box component="span" aria-hidden sx={{ display: 'flex', color: tokens.warningFg }}>
          <LeafIcon />
        </Box>
        {t('home.nudge.title')}
      </Typography>
      {line === null || cta === null ? (
        <Box aria-busy="true">
          <Skeleton variant="text" />
          <Skeleton variant="rounded" height={44} sx={{ mt: 1 }} />
        </Box>
      ) : (
        <>
          <Typography
            component="p"
            data-nudge-line=""
            sx={{ m: 0, fontSize: 14, lineHeight: '20px', color: tokens.text2 }}
          >
            {line}
          </Typography>
          {/* A flat primary button about 38 px tall (TMMAPP-0154 item 29). */}
          <Button
            variant="contained"
            fullWidth
            component={RouterLink}
            to={cta.to}
            data-nudge-cta=""
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: 14,
              lineHeight: '20px',
              borderRadius: '12px',
              py: '9px',
            }}
          >
            {cta.label}
          </Button>
          {nudge?.kind !== 'calm' && (
            <Button
              onClick={onDismiss}
              sx={{ alignSelf: 'center', fontSize: 13, fontWeight: 400, textTransform: 'none' }}
            >
              {t('home.nudge.notNow')}
            </Button>
          )}
        </>
      )}
    </Paper>
  );
}

export function ComingUpCard({
  meetings,
  next,
  onRetry,
  timeZone,
}: {
  meetings: Read<MeetingCalendar>;
  next: MeetingCalendarEntry | null;
  onRetry: () => void;
  timeZone: string;
}) {
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  const titleId = useId();

  let body;
  if (meetings.status === 'loading') {
    body = <Skeleton variant="rounded" height={44} />;
  } else if (meetings.status === 'error') {
    body = (
      <ErrorState
        variant="section"
        error={meetings.error}
        what={t('errors.what.comingUp')}
        onRetry={onRetry}
      />
    );
  } else if (next === null) {
    body = (
      <Typography
        variant="body2"
        data-coming-up-empty=""
        sx={{ py: '10px', borderTop: '1px solid', borderColor: tokens.line, color: tokens.text2 }}
      >
        {t('home.comingUp.empty')}
      </Typography>
    );
  } else {
    const { date, time } = meetingDateTime(next, locale, timeZone);
    const minutes = meetingMinutes(next);
    const sub =
      minutes === null
        ? t('home.comingUp.whenNoDuration', { date, time })
        : t('home.comingUp.when', {
            date,
            time,
            duration: t('home.comingUp.minutes', { count: minutes }),
          });
    body = (
      <Box
        component={RouterLink}
        to={meetingSessionPath(next)}
        data-coming-up=""
        sx={{
          display: 'flex',
          gap: '12px',
          alignItems: 'flex-start',
          py: '10px',
          borderTop: '1px solid',
          borderColor: tokens.line,
          color: tokens.text,
          textDecoration: 'none',
          '&:hover [data-row-title]': { textDecoration: 'underline' },
          '&:focus-visible': { outline: `2px solid ${tokens.focusRing}`, outlineOffset: 2 },
        }}
      >
        <Box
          component="span"
          aria-hidden
          sx={{
            width: 34,
            height: 34,
            flex: '0 0 34px',
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: tintFor('peach', tokens).soft,
            color: tokens.text,
          }}
        >
          <MeetingsIcon />
        </Box>
        <Box
          component="span"
          sx={{ display: 'flex', flexDirection: 'column', gap: '1px', minWidth: 0 }}
        >
          <Typography
            component="span"
            data-row-title=""
            sx={{ fontWeight: 600, fontSize: 14, lineHeight: '20px', overflowWrap: 'anywhere' }}
          >
            {next.title}
          </Typography>
          <Typography
            component="span"
            sx={{ fontSize: 12.5, lineHeight: '18px', color: tokens.textLabel('surface') }}
          >
            {sub}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Paper component="section" aria-labelledby={titleId} sx={{ ...CARD_SX, gap: '10px' }}>
      <Typography
        id={titleId}
        variant="overline"
        component="h2"
        sx={{ color: tokens.textLabel('surface') }}
      >
        {t('home.comingUp.title')}
      </Typography>
      {body}
    </Paper>
  );
}
